/**
 * Filing a transaction: pick one category, or split it across several.
 *
 * Every option shows what is left in it, because "which envelope?" is really
 * "which envelope can afford this?". Sorted by remaining, most available first,
 * so the common answer is near the top.
 */

import { useMemo, useState } from 'react';
import {
  evenSplit,
  formatAmountInput,
  formatMoney,
  itemPathLabel,
  merchantName,
  parseMoney,
  validateSplits,
  type BudgetItem,
  type BudgetItemBalance,
  type Transaction,
} from '@alohamula/domain';
import { Badge, Button, Dialog, Field, Input, Money, MoneyInput, Select } from './ui';
import { PlusIcon, SplitIcon, TrashIcon } from './icons';
import { colorVar } from '../budget/colors';
import { AskAboutCharge } from './AskAboutCharge';
import { useCreateBudgetItem } from '../api/queries';
import { useToast } from './ui/ToastProvider';
import './transactions.css';

interface CategorizeDialogProps {
  transaction: Transaction | null;
  envelopes: BudgetItemBalance[];
  bills: BudgetItemBalance[];
  /** Already-filed categories, so reopening a split shows the current state. */
  initialSplits?: { budgetItemId: string; amount: number }[];
  busy?: boolean;
  onClose: () => void;
  onSubmit: (allocations: { budgetItemId: string; amount: number }[]) => void;
}

export function CategorizeDialog({
  transaction,
  envelopes,
  bills,
  initialSplits,
  busy,
  onClose,
  onSubmit,
}: CategorizeDialogProps) {
  const [mode, setMode] = useState<'single' | 'split'>(
    initialSplits && initialSplits.length > 1 ? 'split' : 'single'
  );

  if (!transaction) return null;

  const total = Math.abs(transaction.amount);

  return (
    <Dialog
      open
      title={merchantName(transaction.description)}
      subtitle={`${formatMoney(total)} · ${mode === 'single' ? 'Choose one category' : 'Divide across categories'}`}
      onClose={onClose}
      footer={
        mode === 'single' ? (
          <>
            <Button emphasis="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button emphasis="secondary" onClick={() => setMode('split')}>
              <SplitIcon width={16} height={16} />
              Split instead
            </Button>
          </>
        ) : undefined
      }
    >
      {mode === 'single' ? (
        <>
          <AskAboutCharge
            transactionId={transaction.id}
            busy={busy}
            onFile={(budgetItemId) => onSubmit([{ budgetItemId, amount: total }])}
          />
          <SinglePicker
            envelopes={envelopes}
            bills={bills}
            selectedId={initialSplits?.length === 1 ? initialSplits[0].budgetItemId : undefined}
            suggestedName={merchantName(transaction.description)}
            onPick={(budgetItemId) => onSubmit([{ budgetItemId, amount: total }])}
          />
        </>
      ) : (
        <SplitEditor
          total={total}
          envelopes={envelopes}
          bills={bills}
          initialSplits={initialSplits}
          busy={busy}
          onBack={() => setMode('single')}
          onSubmit={onSubmit}
        />
      )}
    </Dialog>
  );
}

/** "$96.29 left", or "$4.99 over" — never "-$4.99 left". */
function availabilityLabel(balance: BudgetItemBalance): string {
  return balance.remaining < 0
    ? `${formatMoney(Math.abs(balance.remaining))} over`
    : `${formatMoney(balance.remaining)} left`;
}

function SinglePicker({
  envelopes,
  bills,
  selectedId,
  suggestedName,
  onPick,
}: {
  envelopes: BudgetItemBalance[];
  bills: BudgetItemBalance[];
  selectedId?: string;
  suggestedName: string;
  onPick: (budgetItemId: string) => void;
}) {
  const combined = useMemo(
    () =>
      [...envelopes, ...bills].sort((a, b) =>
        a.item.name.localeCompare(b.item.name, undefined, { numeric: true, sensitivity: 'base' })
      ),
    [envelopes, bills]
  );
  const items = combined.map((balance) => balance.item);

  return (
    <div className="picker">
      <NewUnderForm items={items} suggestedName={suggestedName} onCreated={onPick} />
      <PickerGroup
        label="A–Z"
        balances={combined}
        items={items}
        selectedId={selectedId}
        onPick={onPick}
      />
    </div>
  );
}

function NewUnderForm({
  items,
  suggestedName,
  onCreated,
}: {
  items: BudgetItem[];
  suggestedName: string;
  onCreated: (budgetItemId: string) => void;
}) {
  const create = useCreateBudgetItem();
  const toast = useToast();
  const roots = items.filter((item) => !item.parentId);
  const [parentId, setParentId] = useState('');
  const [name, setName] = useState(suggestedName);
  const [open, setOpen] = useState(false);

  if (roots.length === 0) return null;

  async function save() {
    const trimmed = name.trim();
    const parent = roots.find((item) => item.id === parentId);
    if (!trimmed || !parent) return;
    try {
      const created = await create.mutateAsync({
        kind: parent.kind,
        name: trimmed,
        icon: parent.icon,
        color: parent.color,
        category: parent.category,
        perPaycheckAmount: 0,
        parentId: parent.id,
        sortOrder: 0,
      });
      toast.show(`Added ${parent.name} › ${created.name}`);
      onCreated(created.id);
    } catch (error) {
      toast.show(error instanceof Error ? error.message : 'Could not create this.');
    }
  }

  return (
    <div className="picker__group">
      <p className="picker__grouplabel">New sub-envelope</p>
      {open ? (
        <div className="stack" style={{ gap: 'var(--space-sm)' }}>
          <Field label="Under">
            {({ id }) => (
              <Select id={id} value={parentId} onChange={(event) => setParentId(event.target.value)}>
                <option value="">Choose…</option>
                {roots.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Name">
            {({ id }) => (
              <Input
                id={id}
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Coffee"
              />
            )}
          </Field>
          <Button
            emphasis="primary"
            size="sm"
            loading={create.isPending}
            disabled={!name.trim() || !parentId}
            onClick={() => void save()}
          >
            Create and file
          </Button>
        </div>
      ) : (
        <Button emphasis="secondary" size="sm" onClick={() => setOpen(true)}>
          <PlusIcon width={16} height={16} />
          New under an envelope
        </Button>
      )}
    </div>
  );
}

function PickerGroup({
  label,
  balances,
  items,
  selectedId,
  onPick,
}: {
  label: string;
  balances: BudgetItemBalance[];
  items: BudgetItem[];
  selectedId?: string;
  onPick: (budgetItemId: string) => void;
}) {
  // Most money available first: usually the answer, and it puts the empty ones
  // where they cannot be picked by accident.
  const sorted = useMemo(
    () => [...balances].sort((a, b) => b.remaining - a.remaining),
    [balances]
  );

  if (sorted.length === 0) return null;

  return (
    <div className="picker__group">
      <p className="picker__grouplabel">{label}</p>
      {sorted.map((balance) => (
        <button
          key={balance.item.id}
          type="button"
          className="pick"
          aria-pressed={balance.item.id === selectedId}
          /* Spelled out rather than assembled from the visible fragments, which
             a screen reader would read as "Groceries $0.00 of $600.00 used
             $600.00". Same sentence the iOS row announces. */
          aria-label={
            balance.remaining < 0
              ? `${itemPathLabel(balance.item, items)}, ${balance.item.kind}, over budget by ${formatMoney(Math.abs(balance.remaining))}`
              : `${itemPathLabel(balance.item, items)}, ${balance.item.kind}, ${formatMoney(balance.remaining)} left`
          }
          onClick={() => onPick(balance.item.id)}
        >
          <span className="dot" style={{ background: colorVar(balance.item.color) }} aria-hidden="true" />
          <span>
            <span className="pick__name" title={itemPathLabel(balance.item, items)}>
              {itemPathLabel(balance.item, items)}
            </span>
            <br />
            <span className="pick__sub">
              {balance.item.kind === 'bill' ? 'Bill' : 'Envelope'}
              {' · '}
              {balance.isOverBudget
                ? `Over by ${formatMoney(balance.spent - balance.budgeted)}`
                : `${formatMoney(balance.spent)} of ${formatMoney(balance.budgeted)} used`}
            </span>
          </span>
          {/*
            One line, trailing-aligned, like BudgetItemPickRow on iOS. The word
            "left" used to sit on a second line under the number, which left an
            orphan and made every row two different heights on the right. What
            the number means is already stated by the "of $600.00 used" line.
          */}
          <span className="pick__left">
            {balance.remaining < 0 && <span className="pick__over">Over</span>}
            <Money
              value={Math.abs(balance.remaining)}
              tone={balance.remaining < 0 ? 'over' : 'default'}
            />
          </span>
        </button>
      ))}
    </div>
  );
}

interface Draft {
  key: number;
  budgetItemId: string;
  /** Kept as a string so a half-typed "12." is not destroyed mid-keystroke. */
  amount: string;
}

let nextKey = 1;

function SplitEditor({
  total,
  envelopes,
  bills,
  initialSplits,
  busy,
  onBack,
  onSubmit,
}: {
  total: number;
  envelopes: BudgetItemBalance[];
  bills: BudgetItemBalance[];
  initialSplits?: { budgetItemId: string; amount: number }[];
  busy?: boolean;
  onBack: () => void;
  onSubmit: (allocations: { budgetItemId: string; amount: number }[]) => void;
}) {
  const [drafts, setDrafts] = useState<Draft[]>(() => {
    if (initialSplits && initialSplits.length > 1) {
      return initialSplits.map((split) => ({
        key: nextKey++,
        budgetItemId: split.budgetItemId,
        amount: formatAmountInput(split.amount),
      }));
    }
    // Two halves that sum exactly, so the only thing left to do is pick names.
    const [first, second] = evenSplit(total, 2);
    return [
      { key: nextKey++, budgetItemId: initialSplits?.[0]?.budgetItemId ?? '', amount: formatAmountInput(first) },
      { key: nextKey++, budgetItemId: '', amount: formatAmountInput(second) },
    ];
  });

  const parsed = drafts.map((draft) => ({
    budgetItemId: draft.budgetItemId,
    amount: parseMoney(draft.amount) ?? 0,
  }));
  const validation = validateSplits(parsed, total);
  const allAmountsParse = drafts.every((draft) => parseMoney(draft.amount) != null);

  const update = (key: number, patch: Partial<Draft>) =>
    setDrafts((current) =>
      current.map((draft) => (draft.key === key ? { ...draft, ...patch } : draft))
    );

  const options = [...envelopes, ...bills].sort((a, b) =>
    a.item.name.localeCompare(b.item.name, undefined, { numeric: true, sensitivity: 'base' })
  );

  return (
    <div className="split">
      {/* Column headers, since each row's own label would be repetitive noise.
          Every control still carries its own aria-label for screen readers. */}
      <div className="split__row" aria-hidden="true">
        <span className="picker__grouplabel">Category</span>
        <span className="picker__grouplabel">Amount</span>
        <span />
      </div>

      {drafts.map((draft, position) => (
        <div className="split__row" key={draft.key}>
          <Select
            aria-label={`Category for part ${position + 1}`}
            value={draft.budgetItemId}
            onChange={(event) => update(draft.key, { budgetItemId: event.target.value })}
          >
            <option value="">Choose…</option>
            {options.map((balance) => (
              <option key={balance.item.id} value={balance.item.id}>
                {itemPathLabel(balance.item, options.map((option) => option.item))} ({
                  balance.item.kind === 'bill' ? 'bill' : 'envelope'
                }) —{' '}
                {availabilityLabel(balance)}
              </option>
            ))}
          </Select>

          <MoneyInput
            aria-label={`Amount for part ${position + 1}`}
            value={draft.amount}
            onChange={(event) => update(draft.key, { amount: event.target.value })}
          />

          <Button
            emphasis="ghost"
            size="sm"
            aria-label={`Remove part ${position + 1}`}
            disabled={drafts.length <= 2}
            onClick={() => setDrafts((current) => current.filter((d) => d.key !== draft.key))}
          >
            <TrashIcon width={16} height={16} />
          </Button>
        </div>
      ))}

      <div className="row" style={{ gap: 'var(--space-xs)' }}>
        <Button
          emphasis="secondary"
          size="sm"
          onClick={() =>
            setDrafts((current) => [
              ...current,
              { key: nextKey++, budgetItemId: '', amount: '0.00' },
            ])
          }
        >
          <PlusIcon width={16} height={16} />
          Add a part
        </Button>
        <Button
          emphasis="ghost"
          size="sm"
          onClick={() =>
            setDrafts((current) => {
              const even = evenSplit(total, current.length);
              return current.map((draft, position) => ({
                ...draft,
                amount: formatAmountInput(even[position]),
              }));
            })
          }
        >
          Split evenly
        </Button>
      </div>

      <div className="split__total">
        <span>
          {formatMoney(validation.total)} of {formatMoney(total)}
        </span>
        {validation.difference === 0 ? (
          <Badge tone="success">Balanced</Badge>
        ) : (
          <Badge tone="warning">
            {validation.difference > 0 ? 'Over by ' : 'Short by '}
            {formatMoney(Math.abs(validation.difference))}
          </Badge>
        )}
      </div>

      {!allAmountsParse && (
        <p className="split__problem" role="alert">
          Amounts must be numbers, like 12.50.
        </p>
      )}
      {allAmountsParse &&
        validation.problems.map((problem) => (
          <p className="split__problem" role="alert" key={problem.code}>
            {problem.message}
          </p>
        ))}

      <div className="row" style={{ gap: 'var(--space-xs)', justifyContent: 'flex-end' }}>
        <Button emphasis="ghost" onClick={onBack}>
          Back
        </Button>
        <Button
          emphasis="primary"
          loading={busy}
          disabled={!validation.isValid || !allAmountsParse}
          onClick={() => onSubmit(parsed)}
        >
          Save split
        </Button>
      </div>
    </div>
  );
}
